import React, {Fragment, useState} from 'react';

import {withRouter} from 'react-router';

import {type AcceptanceType} from 'app/acceptance/types';
import {RTKDocumentTeamsSidebar} from 'app/components/general/RTKDocumentTeamsSidebar';
import {DetailPageSidebarShortcuts} from 'app/general/shortcuts/DetailPageSidebarShortcuts';
import {PageSidebarStructure} from 'app/general/structures/PageSidebarStructure';
import {SidebarItem} from 'app/general/structures/SidebarItem';
import VersionsSidebarItem from 'app/procedures/components/ProcedureDetailPage/ProcedurePageSidebar/VersionsSidebarItem';
import {currVersionTooltipTextSelector} from 'app/procedures/selectors';
import {QuizPoliciesSidebar} from 'app/quizzes/components/QuizDetailPage/QuizPoliciesSidebar';
import {QuizProceduresSidebar} from 'app/quizzes/components/QuizDetailPage/QuizProceduresSidebar';
import {QuizProcessesSidebar} from 'app/quizzes/components/QuizDetailPage/QuizProcessesSidebar';
import {QuizVersionsSidebar} from 'app/quizzes/components/QuizVersionsSidebar';
import {
  getObjectCurrentVersionURL,
  getObjectTeamsURL,
  getQuizAttemptsURL,
  getQuizPoliciesURL,
  getQuizProceduresURL,
  getQuizProcessesURL,
} from 'app/urls';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';
import {getPathFromURL} from 'app/utils/urls';

import {QuizAttemptSidebar} from './QuizAttemptsSidebar';

import type {WithRouterProps} from 'react-router';
import type {QuizSerializedType, QuizVersionSerializedType} from 'sweet-types';

const policyIcon = getContentType('policy').icon;
const procedureIcon = getContentType('procedure').icon;
const quizIcon = getContentType('quiz').icon;
const processIcon = getContentType('proceduregroup').icon;
const teamIcon = getContentType('team').icon;
const attemptIcon = getContentType('attempt').icon;

interface Props extends WithRouterProps {
  quiz: QuizSerializedType;
  versionId?: number | string | null;
  acceptance?: AcceptanceType;
  version: QuizVersionSerializedType | undefined;
  versions: Array<QuizVersionSerializedType>;
  route: {
    props: {
      showVersions?: boolean;
      showProcedures?: boolean;
      showPolicies?: boolean;
      showAttempts?: boolean;
      showTeams?: boolean;
      showProcesses?: boolean;
    };
  };
  userId?: number;
}

export const QuizPageSidebar = withRouter((props: Props) => {
  const [collapsed, setCollapsed] = useState(false);
  const {quiz, version, versionId, route, router, location, versions, params} =
    props;

  const {
    showVersions,
    showTeams,
    showAttempts,
    showProcesses,
    showPolicies,
    showProcedures,
  } = route.props;

  const closeInnerSidebar = () => {
    router.replace(quiz.html_url);
  };

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const toggleVersionsList = () => {
    setCollapsed(false);
  };

  const getVersionsURL = (): string => {
    return getPathFromURL(
      getObjectCurrentVersionURL(quiz, versionId, location.search)
    );
  };

  const getProceduresURL = (): string => {
    return getQuizProceduresURL(quiz);
  };

  const getProcessesURL = () => getQuizProcessesURL(quiz);

  const getPoliciesURL = () => getQuizPoliciesURL(quiz);

  const getAttemptsURL = () => getQuizAttemptsURL(quiz);

  const getTeamURL = (): string => {
    return getObjectTeamsURL(quiz, location.search);
  };

  const goToEditURL = () => {
    router.push(`${quiz.html_url}edit/`);
  };

  return (
    <DetailPageSidebarShortcuts
      openVersions={() => router.push(getVersionsURL())}
      openComments={() => null}
      openActivities={() => router.push(getAttemptsURL())}
      edit={goToEditURL}
    >
      <PageSidebarStructure
        innerContent={
          showVersions === true ? (
            <QuizVersionsSidebar
              canDelete={quiz.can_delete}
              collapsed={collapsed}
              onToggleCollapsed={toggleCollapsed}
              selectedVersion={version}
              closeSidebar={closeInnerSidebar}
              objectId={quiz.id}
            />
          ) : showPolicies === true ? (
            <QuizPoliciesSidebar
              canEdit={quiz.can_edit}
              quizId={quiz.id}
              quizURL={quiz.url}
              attachedItems={quiz.links.filter(
                (link) => link.content_type == 'policy'
              )}
              onClose={closeInnerSidebar}
            />
          ) : showProcedures === true ? (
            <QuizProceduresSidebar
              canEdit={quiz.can_edit}
              quizId={quiz.id}
              quizURL={quiz.url}
              attachedItems={quiz.links.filter(
                (link) => link.content_type == 'procedure'
              )}
              onClose={closeInnerSidebar}
            />
          ) : showProcesses === true ? (
            <QuizProcessesSidebar
              canEdit={quiz.can_edit}
              quizId={quiz.id}
              quizURL={quiz.url}
              attachedItems={quiz.links.filter(
                (link) => link.content_type == 'proceduregroup'
              )}
              onClose={closeInnerSidebar}
            />
          ) : showAttempts === true ? (
            <QuizAttemptSidebar
              canDelete={quiz.can_delete}
              quizId={quiz.id}
              attemptId={params.attemptid}
              onClose={closeInnerSidebar}
            />
          ) : showTeams === true ? (
            <RTKDocumentTeamsSidebar
              contentType="quiz"
              onClose={closeInnerSidebar}
              canEdit={quiz.can_edit}
              url={quiz.url}
              id={quiz.id}
              team_memberships={quiz.team_memberships}
            />
          ) : null
        }
      >
        {(showTooltip, hideTooltip) => {
          const commonProps = {
            forceTooltipShow: showTooltip,
            onClick: hideTooltip,
          };

          const versionSidebarProps = {
            ...commonProps,
            onClick: toggleVersionsList,
          };

          return (
            <Fragment>
              <SidebarItem
                title={currVersionTooltipTextSelector(quiz)}
                icon={quizIcon}
                onlyActiveOnIndex={true}
                link={getPathFromURL(quiz.html_url)}
                {...commonProps}
              />
              {quiz.can_change && (
                <>
                  <VersionsSidebarItem
                    link={getVersionsURL()}
                    collapsed={collapsed}
                    active={showVersions}
                    versions={versions}
                    {...versionSidebarProps}
                  />
                  <SidebarItem
                    title={gt('Related Policies')}
                    icon={policyIcon}
                    link={getPoliciesURL}
                    {...commonProps}
                  />
                  <SidebarItem
                    title={gt('Related Procedures')}
                    icon={procedureIcon}
                    link={getProceduresURL}
                    {...commonProps}
                  />
                  <SidebarItem
                    title={gt('Related Processes')}
                    icon={processIcon}
                    link={getProcessesURL}
                    {...commonProps}
                  />
                  <SidebarItem
                    title={gt('Related Teams')}
                    active={showTeams}
                    icon={teamIcon}
                    link={getTeamURL}
                    {...commonProps}
                  />
                </>
              )}
              <SidebarItem
                title={gt('Attempts')}
                active={showAttempts}
                icon={attemptIcon}
                link={getAttemptsURL}
                {...commonProps}
              />
            </Fragment>
          );
        }}
      </PageSidebarStructure>
    </DetailPageSidebarShortcuts>
  );
});
